import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api, { BACKEND_URL } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { VideoAnalysisCard } from '@/components/VideoAnalysis';
import { toast } from 'sonner';
import {
  BookOpen, Video, Plus, Play, Mic, MicOff, Volume2, VolumeX,
  Star, CheckCircle2, Waves, ChevronRight, Trash2, Type, Upload, RefreshCw, AlertCircle, Edit3, Save
} from 'lucide-react';

// Lazy video component - only loads when visible in viewport
function LazyVideo({ src, className, muted = true, controls = true, objectFit = 'contain', onError, style, ...props }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleError = useCallback(() => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.src = src + (src.includes('?') ? '&' : '?') + 'retry=' + (retryCount + 1);
          videoRef.current.load();
        }
      }, 1000 * (retryCount + 1));
    } else {
      setHasError(true);
    }
  }, [retryCount, src]);

  const manualRetry = () => {
    setHasError(false);
    setRetryCount(0);
    if (videoRef.current) {
      videoRef.current.src = src + (src.includes('?') ? '&' : '?') + 'retry=manual';
      videoRef.current.load();
    }
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className || ''}`} style={style}>
      {hasError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 rounded-xl">
          <p className="text-xs text-slate-400 mb-2">Video unavailable</p>
          <button onClick={manualRetry} className="flex items-center gap-1 text-xs text-[#22D3EE] hover:underline font-semibold">
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      ) : isVisible ? (
        <video
          ref={videoRef}
          src={src}
          className={`w-full h-full ${objectFit === 'cover' ? 'object-cover' : 'object-contain'}`}
          muted={muted}
          controls={controls}
          preload="metadata"
          playsInline
          onError={handleError}
          {...props}
        />
      ) : (
        <div className="w-full h-full bg-slate-100 animate-pulse rounded-xl flex items-center justify-center">
          <Play className="w-6 h-6 text-slate-300" />
        </div>
      )}
    </div>
  );
}

export default function StoryboardPage() {
  const { childId } = useParams();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [storyboards, setStoryboards] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [videos, setVideos] = useState([]);
  const [showCreateEntry, setShowCreateEntry] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [entryText, setEntryText] = useState('');
  const [entrySkillId, setEntrySkillId] = useState('');
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mutedVideos, setMutedVideos] = useState({});
  const [useTTS, setUseTTS] = useState(false);
  const [showUploadVideo, setShowUploadVideo] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const fetchData = useCallback(async () => {
    try {
      const [sessRes, sbRes] = await Promise.all([
        api.get(`/sessions?child_id=${childId}`),
        api.get(`/storyboards?child_id=${childId}`)
      ]);
      setSessions(sessRes.data);
      setStoryboards(sbRes.data);
      if (sessRes.data.length > 0) {
        const lastCompleted = [...sessRes.data].reverse().find(s => s.status === 'completed') || sessRes.data[sessRes.data.length - 1];
        setActiveSession(lastCompleted);
        loadVideos(lastCompleted.session_id);
      }
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, [childId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadVideos = async (sessionId) => {
    try {
      const res = await api.get(`/videos?session_id=${sessionId}`);
      setVideos(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };

  const handleSessionChange = (sessionId) => {
    const sess = sessions.find(s => s.session_id === sessionId);
    setActiveSession(sess);
    loadVideos(sessionId);
  };

  const getStoryboard = () => storyboards.find(sb => sb.session_id === activeSession?.session_id);

  const createStoryboard = async () => {
    try {
      const res = await api.post('/storyboards', { session_id: activeSession.session_id, child_id: childId });
      setStoryboards(prev => [...prev, res.data]);
      toast.success('Storyboard created!');
    } catch { toast.error('Failed to create storyboard'); }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const speakText = (text) => {
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.8;
    utterance.pitch = 1.1;
    speechSynthesis.speak(utterance);
  };

  const addStoryEntry = async () => {
    if (!selectedVideo) { toast.error('Select a video first'); return; }

    let sb = getStoryboard();
    // Auto-create storyboard if it doesn't exist yet
    if (!sb) {
      try {
        const res = await api.post('/storyboards', { session_id: activeSession.session_id, child_id: childId });
        setStoryboards(prev => [...prev, res.data]);
        sb = res.data;
      } catch { toast.error('Failed to create storyboard'); return; }
    }

    let audioFilename = null;
    if (audioBlob) {
      const formData = new FormData();
      formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
      const audioRes = await api.post('/audio/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      audioFilename = audioRes.data.filename;
    }

    try {
      await api.post(`/storyboards/${sb.storyboard_id}/entries`, {
        video_id: selectedVideo.video_id,
        skill_id: entrySkillId || selectedVideo.skill_id,
        text: entryText,
        audio_filename: audioFilename,
        order: (sb.entries?.length || 0) + 1
      });
      toast.success('Story added!');
      setShowCreateEntry(false);
      setSelectedVideo(null);
      setEntryText('');
      setEntrySkillId('');
      setAudioBlob(null);
      fetchData();
    } catch { toast.error('Failed to add story'); }
  };

  const deleteEntry = async (entryId) => {
    const sb = getStoryboard();
    if (!sb) return;
    try {
      await api.delete(`/storyboards/${sb.storyboard_id}/entries/${entryId}`);
      fetchData();
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };

  const [uploadSkillName, setUploadSkillName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadNotes, setUploadNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [editNotesText, setEditNotesText] = useState('');
  const [requestingVideo, setRequestingVideo] = useState(null);

  const handleVideoUpload = async () => {
    if (!uploadFile || !activeSession) return;
    setUploadingVideo(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('session_id', activeSession.session_id);
    formData.append('child_id', childId);
    const matchedSkill = (activeSession.skills || []).find(s => s.name === uploadSkillName);
    formData.append('skill_id', matchedSkill?.skill_id || '');
    formData.append('skill_name', uploadSkillName.trim() || 'General');
    formData.append('notes', uploadNotes.trim());
    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success('Video uploaded!');
      setShowUploadVideo(false);
      setUploadFile(null);
      setUploadSkillName('');
      setUploadProgress(0);
      setUploadNotes('');
      loadVideos(activeSession.session_id);
    } catch { toast.error('Upload failed. Try a smaller file or check your connection.'); }
    finally { setUploadingVideo(false); }
  };

  const toggleMute = (videoId) => {
    setMutedVideos(prev => ({ ...prev, [videoId]: !prev[videoId] }));
  };

  if (sessions.length === 0) {
    return (
      <div className="text-center py-16" data-testid="no-sessions">
        <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Sessions Yet</h2>
      </div>
    );
  }

  const storyboard = getStoryboard();
  const coveredSkills = new Set((storyboard?.entries || []).map(e => e.skill_id));

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn" data-testid="storyboard-page">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mb-6" style={{ fontFamily: 'Nunito' }}>
        Storyboard
      </h1>

      {/* Session Tabs */}
      <Tabs value={activeSession?.session_id} onValueChange={handleSessionChange} className="mb-6">
        <TabsList className="bg-white border border-slate-100 rounded-2xl p-1 flex-wrap h-auto gap-1" data-testid="session-tabs">
          {sessions.map(s => (
            <TabsTrigger key={s.session_id} value={s.session_id} className="rounded-xl data-[state=active]:bg-[#22D3EE] data-[state=active]:text-white font-semibold px-4">
              Session {s.session_number}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {activeSession && (
        <>
          {/* Visual Schedule for this session */}
          <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="session-schedule">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Session Schedule</h3>
              <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                {(activeSession.skills || []).map((skill, idx) => (
                  <div key={skill.skill_id} className="flex items-center gap-1.5 flex-shrink-0">
                    <div className={`flex flex-col items-center justify-center w-[72px] h-[72px] sm:w-20 sm:h-20 rounded-xl border-2 text-center
                      ${coveredSkills.has(skill.skill_id) ? 'border-green-300 bg-green-50' : skill.completed ? 'border-[#C4F017] bg-[#FFFBEB]' : 'border-slate-200 bg-white'}`}>
                      <span className="text-lg">{coveredSkills.has(skill.skill_id) ? '✅' : skill.completed ? '⭐' : '⬜'}</span>
                      <span className="text-[9px] font-bold text-slate-600 leading-tight mt-0.5 px-1">{skill.name}</span>
                    </div>
                    {idx < activeSession.skills.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Video Bank */}
          <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="video-bank">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                  <Video className="w-5 h-5 text-[#22D3EE]" /> Video Bank
                  <Badge variant="outline" className="rounded-full ml-2">{videos.length} videos</Badge>
                </CardTitle>
                {user?.role === 'instructor' && activeSession && (
                  <div className="relative">
                    <Button
                      data-testid="upload-video-bank-btn"
                      onClick={() => setShowUploadVideo(true)}
                      className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-white font-bold gap-2 text-sm"
                    >
                      <Upload className="w-4 h-4" /> Upload Video
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {videos.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No videos for this session yet</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map(v => (
                    <div key={v.video_id} className="relative group" data-testid={`video-${v.video_id}`}>
                      <div className="aspect-video bg-slate-100 rounded-2xl overflow-hidden relative">
                        <LazyVideo
                          src={`${BACKEND_URL}/api/uploads/${v.filename}`}
                          className="w-full h-full"
                          muted={mutedVideos[v.video_id] !== false}
                          objectFit="cover"
                        />
                        <button
                          onClick={() => toggleMute(v.video_id)}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white"
                        >
                          {mutedVideos[v.video_id] === false ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                        {user?.role === 'instructor' && (
                          <button
                            onClick={async () => { try { await api.delete(`/videos/${v.video_id}`); toast.success('Video deleted'); loadVideos(activeSession.session_id); } catch { toast.error('Failed to delete'); } }}
                            className="absolute top-2 left-2 w-8 h-8 bg-red-500/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-video-${v.video_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs font-bold text-slate-600 mt-2 truncate">{v.skill_name || 'General'}</p>
                      {/* Notes display + inline edit */}
                      {v.notes ? (
                        <div className="mt-1 bg-amber-50 rounded-lg p-2 border border-amber-100">
                          <p className="text-[11px] text-amber-800 leading-tight">{v.notes}</p>
                          {user?.role === 'instructor' && (
                            <button onClick={() => { setEditingNotes(v.video_id); setEditNotesText(v.notes); }} className="text-[10px] text-amber-600 hover:underline mt-1 flex items-center gap-0.5">
                              <Edit3 className="w-2.5 h-2.5" /> Edit
                            </button>
                          )}
                        </div>
                      ) : user?.role === 'instructor' && (
                        <button onClick={() => { setEditingNotes(v.video_id); setEditNotesText(''); }} className="text-[10px] text-slate-400 hover:text-[#22D3EE] mt-1 flex items-center gap-0.5" data-testid={`add-notes-${v.video_id}`}>
                          <Edit3 className="w-2.5 h-2.5" /> Add notes
                        </button>
                      )}
                      {editingNotes === v.video_id && (
                        <div className="mt-1 space-y-1">
                          <textarea
                            value={editNotesText}
                            onChange={e => setEditNotesText(e.target.value)}
                            className="w-full text-xs border rounded-lg p-2 focus:ring-1 focus:ring-[#22D3EE] outline-none resize-none"
                            rows={2}
                            placeholder="e.g., Picked up 6 rings without help!"
                            data-testid={`notes-input-${v.video_id}`}
                          />
                          <div className="flex gap-1">
                            <button onClick={async () => { try { await api.put(`/videos/${v.video_id}/notes`, { notes: editNotesText }); toast.success('Notes saved'); setEditingNotes(null); loadVideos(activeSession.session_id); } catch { toast.error('Failed'); } }} className="text-[10px] bg-[#22D3EE] text-white px-2 py-1 rounded-md font-bold flex items-center gap-0.5" data-testid={`save-notes-${v.video_id}`}>
                              <Save className="w-2.5 h-2.5" /> Save
                            </button>
                            <button onClick={() => setEditingNotes(null)} className="text-[10px] text-slate-400 px-2 py-1">Cancel</button>
                          </div>
                        </div>
                      )}
                      <VideoAnalysisCard videoId={v.video_id} />
                      <Button
                        size="sm"
                        onClick={() => { setSelectedVideo(v); setEntrySkillId(v.skill_id || ''); setShowCreateEntry(true); }}
                        className="mt-2 w-full rounded-xl bg-[#22D3EE]/10 text-[#22D3EE] hover:bg-[#22D3EE]/20 font-semibold text-sm h-10"
                        data-testid={`use-video-${v.video_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Use in Story
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Missing Video? — for parents when skills have no videos */}
          {user?.role === 'parent' && activeSession && (() => {
            const completedSkills = (activeSession.skills || []).filter(s => s.completed);
            const videoSkillIds = new Set(videos.map(v => v.skill_id));
            const missingSkills = completedSkills.filter(s => !videoSkillIds.has(s.skill_id));
            if (missingSkills.length === 0) return null;
            return (
              <Card className="rounded-3xl border-amber-200 bg-amber-50/50 shadow-sm mb-6" data-testid="missing-videos-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <h3 className="text-sm font-bold text-amber-800">Missing Videos</h3>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">{missingSkills.length} completed skill{missingSkills.length !== 1 ? 's are' : ' is'} missing video{missingSkills.length !== 1 ? 's' : ''}. Tap to notify the instructor.</p>
                  <div className="space-y-2">
                    {missingSkills.map(skill => (
                      <button
                        key={skill.skill_id}
                        disabled={requestingVideo === skill.skill_id}
                        onClick={async () => {
                          setRequestingVideo(skill.skill_id);
                          try {
                            await api.post('/videos/request-missing', {
                              child_id: childId,
                              session_id: activeSession.session_id,
                              skill_name: skill.name,
                            });
                            toast.success(`Request sent to instructor for "${skill.name}"`);
                          } catch { toast.error('Failed to send request'); }
                          finally { setRequestingVideo(null); }
                        }}
                        className="w-full flex items-center justify-between bg-white rounded-xl p-3 border border-amber-200 hover:border-amber-400 transition-colors text-left"
                        data-testid={`request-video-${skill.skill_id}`}
                      >
                        <div>
                          <p className="text-sm font-bold text-slate-700">{skill.name}</p>
                          <p className="text-[10px] text-slate-400">Completed — no video uploaded</p>
                        </div>
                        <span className="text-xs font-bold text-amber-600 whitespace-nowrap">
                          {requestingVideo === skill.skill_id ? 'Sending...' : 'Missing video?'}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Storyboard Timeline */}
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="storyboard-timeline">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Nunito' }}>Story Timeline</CardTitle>
                {!storyboard && videos.length > 0 && (
                  <Button data-testid="create-storyboard-btn" onClick={createStoryboard} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold gap-2">
                    <Plus className="w-4 h-4" /> Start Storyboard
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {storyboard ? (
                storyboard.entries?.length > 0 ? (
                  <div className="space-y-6">
                    {storyboard.entries.map((entry, idx) => {
                      const video = videos.find(v => v.video_id === entry.video_id);
                      const skill = activeSession.skills?.find(s => s.skill_id === entry.skill_id);
                      return (
                        <div key={entry.entry_id} className="flex gap-2 sm:gap-4 animate-fadeIn" data-testid={`story-entry-${entry.entry_id}`}>
                          <div className="flex flex-col items-center shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#22D3EE] text-white flex items-center justify-center font-bold text-sm sm:text-lg" style={{ fontFamily: 'Nunito' }}>{idx + 1}</div>
                            {idx < storyboard.entries.length - 1 && <div className="w-0.5 flex-1 bg-[#22D3EE]/20 mt-2" />}
                          </div>
                          <div className="flex-1 bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                            {video && (
                              <div className="rounded-xl overflow-hidden mb-3 bg-slate-900">
                                <LazyVideo src={`${BACKEND_URL}/api/uploads/${video.filename}`} className="w-full" />
                              </div>
                            )}
                            {skill && <Badge variant="outline" className="rounded-full border-[#22D3EE]/30 text-[#22D3EE] mb-2">{skill.name}</Badge>}
                            {video?.notes && (
                              <div className="bg-amber-50 rounded-lg p-2 border border-amber-100 mb-2">
                                <p className="text-[11px] text-amber-800 font-semibold leading-tight">Instructor note: {video.notes}</p>
                              </div>
                            )}
                            {entry.text && (
                              <div className="flex items-start gap-2">
                                <p className="text-slate-700 flex-1">{entry.text}</p>
                                <button onClick={() => speakText(entry.text)} className="text-[#22D3EE] hover:text-[#06B6D4] p-1 flex-shrink-0" data-testid={`speak-entry-${entry.entry_id}`}>
                                  <Volume2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            {entry.audio_filename && (
                              <audio src={`${BACKEND_URL}/api/uploads/${entry.audio_filename}`} controls className="mt-2 w-full h-8" />
                            )}
                            <div className="flex justify-end mt-2">
                              <Button variant="ghost" size="sm" onClick={() => deleteEntry(entry.entry_id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No stories yet. Click on a video above to start creating!</p>
                )
              ) : (
                <p className="text-center text-slate-400 py-8">
                  {videos.length > 0 ? 'Create a storyboard to start building stories!' : 'Videos will appear here after the session.'}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create Entry Dialog */}
      <Dialog open={showCreateEntry} onOpenChange={setShowCreateEntry}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto mx-2">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Create Story Entry</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {selectedVideo && (
              <LazyVideo src={`${BACKEND_URL}/api/uploads/${selectedVideo.filename}`} className="w-full rounded-2xl" />
            )}

            {/* Assign to skill */}
            <div>
              <label className="text-sm font-semibold text-slate-600 block mb-1">Assign to Skill</label>
              <div className="flex flex-wrap gap-1.5">
                {(activeSession?.skills || []).map(s => (
                  <button
                    key={s.skill_id}
                    onClick={() => setEntrySkillId(s.skill_id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${entrySkillId === s.skill_id ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Text or Voice */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setUseTTS(false)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${!useTTS ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  <Mic className="w-3 h-3" /> Voice
                </button>
                <button
                  onClick={() => setUseTTS(true)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${useTTS ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  <Type className="w-3 h-3" /> Type (TTS)
                </button>
              </div>

              {useTTS ? (
                <div>
                  <Textarea
                    data-testid="story-text-input"
                    className="rounded-xl"
                    placeholder="Type what happened... (will be read aloud)"
                    value={entryText}
                    onChange={e => setEntryText(e.target.value)}
                    rows={3}
                  />
                  {entryText && (
                    <Button size="sm" variant="outline" onClick={() => speakText(entryText)} className="mt-2 rounded-full gap-1">
                      <Volume2 className="w-3 h-3" /> Preview TTS
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Textarea
                    className="rounded-xl"
                    placeholder="Optional text description..."
                    value={entryText}
                    onChange={e => setEntryText(e.target.value)}
                    rows={2}
                  />
                  <div className="flex gap-2">
                    {!recording ? (
                      <Button data-testid="record-voice-btn" onClick={startVoiceRecording} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold gap-2">
                        <Mic className="w-4 h-4" /> Record Voice
                      </Button>
                    ) : (
                      <Button data-testid="stop-voice-btn" onClick={stopVoiceRecording} className="rounded-full bg-red-600 text-white font-bold gap-2">
                        <div className="recording-dot" /> Stop
                      </Button>
                    )}
                    {audioBlob && <Badge className="bg-green-50 text-green-600 border-green-200">Voice recorded</Badge>}
                  </div>
                </div>
              )}
            </div>

            <Button data-testid="save-story-entry-btn" onClick={addStoryEntry} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold">
              Add to Storyboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Video to Bank Dialog */}
      <Dialog open={showUploadVideo} onOpenChange={(v) => { setShowUploadVideo(v); if (!v) { setUploadFile(null); setUploadSkillName(''); } }}>
        <DialogContent className="rounded-3xl max-h-[90vh] max-h-[90dvh] overflow-y-auto mx-2">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Upload Video to Bank</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-500">Upload a video file for Session {activeSession?.session_number}</p>

            {/* Skill/Caption Selection */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Skill / Caption</label>
              {(activeSession?.skills || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {activeSession.skills.map(s => (
                    <button
                      key={s.skill_id}
                      onClick={() => setUploadSkillName(s.name)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${uploadSkillName === s.name ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`select-skill-${s.skill_id}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={uploadSkillName}
                onChange={e => setUploadSkillName(e.target.value)}
                placeholder="Or type a custom caption..."
                className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent outline-none"
                data-testid="upload-skill-caption-input"
              />
            </div>

            {/* Instructor Notes */}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Notes (optional)</label>
              <textarea
                value={uploadNotes}
                onChange={e => setUploadNotes(e.target.value)}
                placeholder="e.g., Picked up 6 rings without any help!"
                className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent outline-none resize-none"
                rows={2}
                data-testid="upload-notes-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Adds context for the parent when building the storyboard</p>
            </div>

            <input
              type="file"
              accept="video/*"
              onChange={e => setUploadFile(e.target.files[0] || null)}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-[#ECFEFF] file:text-[#22D3EE] hover:file:bg-[#BAE6FD] cursor-pointer"
              data-testid="video-bank-file-input"
            />
            {uploadFile && (
              <p className="text-xs text-slate-400">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>
            )}
            {uploadingVideo && (
              <div className="space-y-2">
                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div className="bg-[#22D3EE] h-3 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-xs text-center text-slate-500 font-semibold">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}</p>
              </div>
            )}
            <button
              onClick={handleVideoUpload}
              disabled={!uploadFile || uploadingVideo}
              className="w-full py-2.5 rounded-xl font-bold text-sm bg-[#1E1B4B] text-white hover:bg-[#2D2B66] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              data-testid="confirm-upload-video-btn"
            >
              {uploadingVideo ? <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="opacity-75" /></svg> Uploading...</> : 'Upload Video'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
